import { useState } from 'react';
import type { ReactNode } from 'react';
import { T, AppHeader, Card, Ico, ConfirmSheet } from '../../design';
import type { ToastData } from '../../design';
import { usePortal } from '../../context/PortalContext';
import type { Screen, NavStack } from '../../portal/navigation';
import type { PrimeActor, PrimeMember, PrimeFamilyMember } from '../../types/prime.types';
import { usePrimeSearch, usePrimeActions } from '../../hooks/prime';
import { fmtPhone } from '../../utils';
import { MembersSearch } from './screens/MembersSearch';
import { MemberDetail } from './screens/MemberDetail';

export interface PrimeModuleProps {
  screen: Screen;
  nav: NavStack;
  fireToast: (toast: ToastData) => void;
}

const MAIN_PAD = '18px 14px calc(36px + env(safe-area-inset-bottom))';

function isSearchType(type: string) { return type === 'medlem' || type === 'members-search'; }

export function PrimeModule({ screen, nav, fireToast }: PrimeModuleProps) {
  const portal = usePortal();
  const employeeId = portal.employee?.id ?? null;
  const actor: PrimeActor | null = employeeId != null ? { employeeId } : null;

  const search = usePrimeSearch(actor);
  const actions = usePrimeActions(actor);

  const [query, setQuery] = useState('');
  const [member, setMember] = useState<PrimeMember | null>(null);
  const [pendingRemove, setPendingRemove] = useState<PrimeFamilyMember | null>(null);
  const [pendingCancel, setPendingCancel] = useState(false);
  const [syncing, setSyncing] = useState(false);

  async function handleSearch() {
    const found = await search.search(query);
    if (found) { setMember(found); nav.push({ type: 'members-detail' }); }
  }

  async function handleInviteNew(phone: string) {
    const r = await actions.invite(phone);
    if (r.success) fireToast({ tone: 'success', msg: 'Invitation sendt på SMS' });
    else if (r.code === 'DUPLICATE_INVITE') fireToast({ tone: 'info', msg: 'Invitation er allerede sendt for nylig.' });
    else fireToast({ tone: 'error', msg: r.error ?? 'Kunne ikke sende invitation.' });
  }

  async function handleAddFamily(phone: string) {
    if (!member) return;
    const r = await actions.addFamily(member.handle, phone);
    if (r.success && r.family) { setMember({ ...member, family: r.family }); fireToast({ tone: 'success', msg: 'Familiemedlem inviteret' }); return; }
    if (r.code === 'FAMILY_LIMIT_REACHED') fireToast({ tone: 'error', msg: 'Alle 5 familiepladser er i brug.' });
    else if (r.code === 'DUPLICATE_PHONE') fireToast({ tone: 'error', msg: 'Nummeret er allerede tilknyttet.' });
    else fireToast({ tone: 'error', msg: r.error ?? 'Kunne ikke invitere familiemedlem.' });
  }

  async function confirmRemove() {
    if (!member || !pendingRemove?.id) { setPendingRemove(null); return; }
    const r = await actions.removeFamily(member.handle, pendingRemove.id);
    if (r.success && r.family) { setMember({ ...member, family: r.family }); fireToast({ tone: 'success', msg: 'Familiemedlem fjernet' }); }
    else fireToast({ tone: 'error', msg: r.error ?? 'Kunne ikke fjerne familiemedlem.' });
    setPendingRemove(null);
  }

  async function confirmCancel() {
    if (!member) { setPendingCancel(false); return; }
    const r = await actions.cancel(member.handle);
    if (r.success && r.member) { setMember(r.member); fireToast({ tone: 'success', msg: 'Medlemskab annulleret' }); }
    else fireToast({ tone: 'error', msg: r.error ?? 'Kunne ikke annullere medlemskab.' });
    setPendingCancel(false);
  }

  async function handleReactivate(plan: 'monthly' | 'yearly') {
    if (!member) return;
    const r = await actions.reactivate(member.handle, plan);
    if (r.success && r.member) {
      setMember(r.member);
      fireToast({ tone: 'success', msg: r.mode === 'resubscribe' ? 'Betalings-SMS sendt' : 'Medlemskab genaktiveret' });
    } else {
      fireToast({ tone: 'error', msg: r.error ?? 'Kunne ikke genaktivere medlemskab.' });
    }
  }

  async function handleSync() {
    if (!member) return;
    // `syncing` er en separat flag fra `actions.busy`: den styrer kun
    // sync-knappens "Opdaterer…"-label, så de øvrige handlinger ikke låses visuelt.
    setSyncing(true);
    const r = await actions.sync(member.handle);
    setSyncing(false);
    if (r.success) fireToast({ tone: 'success', msg: 'Bookingsystem opdateret' });
    else if (r.code === 'PHONE_MISSING') fireToast({ tone: 'error', msg: 'Kunden mangler telefonnummer.' });
    else if (r.code === 'BOOKING_SYNC_FAILED') fireToast({ tone: 'error', msg: 'Bookingsystemet svarer ikke — prøv igen om lidt.' });
    else fireToast({ tone: 'error', msg: r.error ?? 'Kunne ikke opdatere bookingsystemet.' });
  }

  let title = 'Find medlem';
  let body: ReactNode = null;

  // En intern bruger når først modulet, når de agerer som en medarbejder (AdminPicker i Shell).
  if (actor == null) {
    body = null;
  } else if (screen.type === 'members-detail' && member) {
    title = member.name;
    body = (
      <MemberDetail
        member={member}
        onAddFamily={handleAddFamily}
        onRemoveFamily={(f) => setPendingRemove(f)}
        onCancel={() => setPendingCancel(true)}
        onReactivate={handleReactivate}
        onSync={handleSync}
        busy={actions.busy}
        syncing={syncing}
      />
    );
  } else {
    // Søgning er default — dækker 'medlem'/'members-search' og
    // members-detail uden member i state (fx efter en OS-back/reload).
    body = (
      <MembersSearch
        query={query}
        setQuery={setQuery}
        onSearch={handleSearch}
        notFound={search.notFound}
        searching={search.searching}
        onInviteNew={handleInviteNew}
      />
    );
  }

  return (
    <>
      <AppHeader eyebrow="PRIME-medlem" title={title} onBack={nav.back} />
      {search.error && isSearchType(screen.type) && (
        <div style={{ padding: '10px 14px 0', background: T.bg }}>
          <Card pad={13} style={{ borderColor: T.errorBorder }}>
            <div style={{ fontSize: 12.5, color: T.error, fontWeight: 600, display: 'flex', gap: 8, alignItems: 'center' }}><Ico.alert size={15} />{search.error}</div>
          </Card>
        </div>
      )}
      <main style={{ flex: 1, padding: MAIN_PAD, background: T.bg }}>{body}</main>

      {pendingRemove && (
        <ConfirmSheet
          title="Fjern familiemedlem?"
          message={`${pendingRemove.name ?? fmtPhone(pendingRemove.phone)} mister adgang til PRIME-fordele.`}
          confirmLabel="Fjern medlem"
          icon="trash"
          onConfirm={confirmRemove}
          onClose={() => setPendingRemove(null)}
        />
      )}
      {pendingCancel && (
        <ConfirmSheet
          title="Annullér medlemskab?"
          message="Medlemskabet opsiges og dækker til periodens udløb."
          note="Eventuelle familiemedlemmer mister også adgang til PRIME-fordele når dækningen ophører."
          confirmLabel="Annullér medlemskab"
          icon="x"
          onConfirm={confirmCancel}
          onClose={() => setPendingCancel(false)}
        />
      )}
    </>
  );
}
